import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  api,
  type KnowledgeItemData,
  type KnowledgePagePreview,
} from "../lib/api";
import { AppError, logError } from "../lib/errors";
import { saveGroupTalkDraft } from "../lib/groupTalkSessionStorage";
import {
  canRetryKnowledgeItemAiPreparation,
  getKnowledgeAiPreparationFailureMessage,
  getKnowledgeAiUsageStatusLabel,
  getKnowledgeBusinessPoints,
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeLibraryStatusLabel,
  getKnowledgePagePreviewLabel,
  getKnowledgeSummaryStatusLabel,
  getKnowledgeTypeLabel,
  isKnowledgeItemAiPreparationFailed,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemPending,
  isKnowledgeItemPreviewable,
  shouldShowKnowledgeSummaryStatus,
} from "../lib/knowledge";
import { buildKnowledgeWallTalkDraft } from "../lib/knowledgeGroupTalkDraft";

const PAGE_PREVIEW_LIMIT = 10;
const DETAIL_POLL_INTERVAL_MS = 5000;
const DETAIL_HIDDEN_POLL_INTERVAL_MS = 15000;
const MAX_URL_POLL_ATTEMPTS = 60;

type DetailError = "not_found" | "load_failed" | null;
type PageError = "not_ready" | "invalid_cursor" | "load_failed" | null;

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function pageErrorFrom(error: unknown): PageError {
  if (error instanceof AppError && (error.code === "CONFLICT" || error.meta.status === 409)) {
    return "not_ready";
  }
  if (error instanceof AppError && error.meta.status === 422) {
    return "invalid_cursor";
  }
  return "load_failed";
}

function pageErrorMessage(error: PageError, loadingMore: boolean): string {
  if (loadingMore) return "続きを読み込めませんでした。";
  if (error === "not_ready") return "本文プレビューはまだ表示できません。";
  return "本文プレビューを読み込めませんでした。時間をおいて、もう一度お試しください。";
}

function SummarySection({ item }: { item: KnowledgeItemData }) {
  const overall = item.summary?.overall?.trim() ?? "";
  const businessPoints = getKnowledgeBusinessPoints(item.summary?.business_points);
  const hasVisibleSummary = overall !== "" || businessPoints.length > 0;

  return (
    <section
      aria-labelledby="knowledge-summary-heading"
      className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm"
    >
      <h2 id="knowledge-summary-heading" className="text-lg font-black text-[#5D4D46]">
        AIが参照する要約
      </h2>
      {!hasVisibleSummary ? (
        <p className="mt-3 text-sm leading-6 text-[#6B5851]">要約はまだありません。</p>
      ) : (
        <div className="mt-4 grid min-w-0 gap-5">
          {overall ? (
            <div className="min-w-0">
              <h3 className="text-sm font-black text-[#7C5A4E]">全体要約</h3>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-7 text-[#4A3E39] [overflow-wrap:anywhere]">
                {overall}
              </p>
            </div>
          ) : null}
          {businessPoints.length > 0 ? (
            <div className="min-w-0">
              <h3 className="text-sm font-black text-[#7C5A4E]">ビジネス上のポイント</h3>
              <ul className="mt-2 grid min-w-0 list-disc gap-2 pl-5 text-sm leading-6 text-[#4A3E39]">
                {businessPoints.map((point, index) => (
                  <li key={`${index}-${point.slice(0, 24)}`} className="break-words [overflow-wrap:anywhere]">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function PagePreviewCard({
  itemType,
  page,
  index,
}: {
  itemType: string;
  page: KnowledgePagePreview;
  index: number;
}) {
  return (
    <article className="min-w-0 rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFDFB] p-4">
      <h3 className="text-sm font-black text-[#7C5A4E]">
        {getKnowledgePagePreviewLabel(itemType, page, index)}
      </h3>
      {page.summary ? (
        <div className="mt-3 min-w-0 rounded-xl bg-[#FFF5EC] px-3 py-3">
          <p className="text-xs font-black text-[#A46D4C]">この部分の要点</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-[#5D4D46] [overflow-wrap:anywhere]">
            {page.summary}
          </p>
        </div>
      ) : null}
      <p className="mt-3 min-w-0 whitespace-pre-wrap break-words text-sm leading-7 text-[#4A3E39] [overflow-wrap:anywhere]">
        {page.preview_text}
      </p>
      {page.is_truncated ? (
        <p className="mt-3 text-xs font-bold text-[#8F7468]">
          この部分はプレビュー上限まで表示しています。
        </p>
      ) : null}
    </article>
  );
}

export default function KnowledgeDetailPage() {
  const navigate = useNavigate();
  const { knowledgeItemId } = useParams<{ knowledgeItemId: string }>();
  const itemId = knowledgeItemId?.trim() ?? "";

  const [item, setItem] = useState<KnowledgeItemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailError, setDetailError] = useState<DetailError>(null);
  const [pages, setPages] = useState<KnowledgePagePreview[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [pagesError, setPagesError] = useState<PageError>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageReloadVersion, setPageReloadVersion] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  const detailGenerationRef = useRef(0);
  const currentItemIdRef = useRef(itemId);
  const loadMoreLockRef = useRef(false);
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  const urlPollAttemptsRef = useRef(0);
  const pollingGenerationRef = useRef(0);
  currentItemIdRef.current = itemId;

  useEffect(() => {
    const controller = new AbortController();
    const generation = detailGenerationRef.current + 1;
    detailGenerationRef.current = generation;
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = null;
    loadMoreLockRef.current = false;
    setLoadingMore(false);
    setRetrying(false);
    setItem(null);
    setLoading(true);
    setDetailError(null);
    setPages([]);
    setNextCursor(null);
    setPagesLoaded(false);
    setPagesError(null);
    setPollingTimedOut(false);
    setRetryMessage(null);
    setRetryError(null);
    urlPollAttemptsRef.current = 0;
    pollingGenerationRef.current += 1;

    if (!itemId) {
      setLoading(false);
      setDetailError("not_found");
      return () => controller.abort();
    }

    void api.getKnowledgeItem(itemId, controller.signal)
      .then((response) => {
        setItem(response);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        logError(error, { action: "knowledge_detail_load" });
        setDetailError(
          error instanceof AppError && (error.code === "NOT_FOUND" || error.meta.status === 404)
            ? "not_found"
            : "load_failed"
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
      if (detailGenerationRef.current !== generation) return;
      loadMoreControllerRef.current?.abort();
      loadMoreControllerRef.current = null;
      loadMoreLockRef.current = false;
    };
  }, [itemId]);

  useEffect(() => {
    if (!item || !isKnowledgeItemPending(item) || pollingTimedOut) return;

    let stopped = false;
    let timeout: number | null = null;
    let controller: AbortController | null = null;
    const generation = pollingGenerationRef.current;

    const schedule = () => {
      if (stopped) return;
      timeout = window.setTimeout(
        () => void poll(),
        document.hidden ? DETAIL_HIDDEN_POLL_INTERVAL_MS : DETAIL_POLL_INTERVAL_MS
      );
    };

    const poll = async () => {
      if (stopped || generation !== pollingGenerationRef.current) return;
      if (document.hidden) {
        schedule();
        return;
      }
      if (item.type === "url" && urlPollAttemptsRef.current >= MAX_URL_POLL_ATTEMPTS) {
        setPollingTimedOut(true);
        return;
      }

      if (item.type === "url") urlPollAttemptsRef.current += 1;
      controller = new AbortController();
      try {
        const response = await api.getKnowledgeItem(itemId, controller.signal);
        if (stopped || generation !== pollingGenerationRef.current) return;
        setItem(response);
        if (isKnowledgeItemPending(response)) {
          schedule();
        }
      } catch (error) {
        if (controller.signal.aborted || stopped) return;
        logError(error, { action: "knowledge_detail_poll" });
        schedule();
      }
    };

    schedule();
    return () => {
      stopped = true;
      if (timeout !== null) window.clearTimeout(timeout);
      controller?.abort();
    };
  }, [item, itemId, pollingTimedOut]);

  const previewable = isKnowledgeItemPreviewable(item);
  const previewItemId = previewable ? item?.id ?? null : null;

  useEffect(() => {
    if (!previewItemId) return;

    const controller = new AbortController();
    setPagesLoading(true);
    setPagesLoaded(false);
    setPagesError(null);
    setPages([]);
    setNextCursor(null);
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = null;
    loadMoreLockRef.current = false;
    setLoadingMore(false);

    void api.listKnowledgeItemPages(previewItemId, { limit: PAGE_PREVIEW_LIMIT }, controller.signal)
      .then((response) => {
        setPages(response.items);
        setNextCursor(response.next_cursor);
        setPagesLoaded(true);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        logError(error, { action: "knowledge_pages_load" });
        setPagesError(pageErrorFrom(error));
        setPagesLoaded(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPagesLoading(false);
      });

    return () => controller.abort();
  }, [previewItemId, pageReloadVersion]);

  const loadMorePages = useCallback(async () => {
    if (!item || !nextCursor || loadMoreLockRef.current) return;
    const requestedItemId = item.id;
    const requestedCursor = nextCursor;
    const requestedGeneration = detailGenerationRef.current;
    const controller = new AbortController();
    loadMoreLockRef.current = true;
    loadMoreControllerRef.current = controller;
    setLoadingMore(true);
    setPagesError(null);
    const isCurrentRequest = () => (
      !controller.signal.aborted
      && loadMoreControllerRef.current === controller
      && detailGenerationRef.current === requestedGeneration
      && currentItemIdRef.current === requestedItemId
    );

    try {
      const response = await api.listKnowledgeItemPages(requestedItemId, {
        limit: PAGE_PREVIEW_LIMIT,
        cursor: requestedCursor,
      }, controller.signal);
      if (!isCurrentRequest()) return;
      setPages((current) => [...current, ...response.items]);
      setNextCursor(response.next_cursor);
    } catch (error) {
      if (!isCurrentRequest()) return;
      logError(error, { action: "knowledge_pages_load_more" });
      setPagesError(pageErrorFrom(error));
    } finally {
      if (loadMoreControllerRef.current !== controller) return;
      loadMoreControllerRef.current = null;
      loadMoreLockRef.current = false;
      setLoadingMore(false);
    }
  }, [item, nextCursor]);

  const retryPages = () => {
    setPagesError(null);
    if (pages.length > 0 && nextCursor) {
      void loadMorePages();
      return;
    }
    setPageReloadVersion((version) => version + 1);
  };

  const handleRetry = async () => {
    if (!item || retrying) return;
    const requestedItemId = item.id;
    const requestedGeneration = detailGenerationRef.current;
    const isCurrentRequest = () => (
      detailGenerationRef.current === requestedGeneration
      && currentItemIdRef.current === requestedItemId
    );
    const retryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
    const canRetryFullUrl = item.type === "url" && item.status === "failed" && item.can_retry;
    const canRetryPdf = item.type === "pdf" && item.status === "failed" && item.can_retry;
    const canRefreshMemo = item.type === "executive_memo" && item.status === "failed" && Boolean(item.source_id);
    if (!retryAiPreparation && !canRetryFullUrl && !canRetryPdf && !canRefreshMemo) return;

    setRetrying(true);
    setRetryMessage(null);
    setRetryError(null);
    setPollingTimedOut(false);
    urlPollAttemptsRef.current = 0;
    pollingGenerationRef.current += 1;

    try {
      const updated = retryAiPreparation || canRetryFullUrl
        ? await api.retryKnowledgeUrl(item.id)
        : canRetryPdf
          ? await api.reprocessKnowledgePdf(item.id)
          : await api.createKnowledgeItemFromExecutiveMemo(item.source_id as string);
      if (!isCurrentRequest()) return;
      setItem(updated);
      if (!isKnowledgeItemPreviewable(updated)) {
        setPages([]);
        setNextCursor(null);
        setPagesLoaded(false);
        setPagesError(null);
      }
      setRetryMessage(retryAiPreparation ? "要約の再試行を開始しました。" : "再処理を開始しました。");
      if (updated.id !== item.id) {
        navigate(`/request/knowledge/${encodeURIComponent(updated.id)}`, { replace: true });
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      logError(error, { action: "knowledge_detail_retry" });
      setRetryError(
        retryAiPreparation
          ? "要約の再試行を開始できませんでした。時間をおいて、もう一度お試しください。"
          : "再処理を開始できませんでした。時間をおいて、もう一度お試しください。"
      );
    } finally {
      if (isCurrentRequest()) setRetrying(false);
    }
  };

  const handleDelete = async () => {
    if (!item || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteKnowledgeItem(item.id);
      navigate("/request/knowledge", { replace: true });
    } catch (error) {
      logError(error, { action: "knowledge_detail_delete" });
      setDeleteError("ナレッジを削除できませんでした。時間をおいて、もう一度お試しください。");
    } finally {
      setDeleting(false);
    }
  };

  const startWallTalk = () => {
    if (!item || !isKnowledgeItemGroupTalkUsable(item)) return;
    const draft = buildKnowledgeWallTalkDraft(item);
    saveGroupTalkDraft(draft);
    navigate("/talk/group", { state: { draft } });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FFF8F2] px-4 py-8">
        <p role="status" className="mx-auto max-w-5xl rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 text-sm font-bold text-[#6B5851]">
          ナレッジの詳細を読み込み中です...
        </p>
      </main>
    );
  }

  if (detailError || !item) {
    return (
      <main className="min-h-screen bg-[#FFF8F2] px-4 py-8">
        <section className="mx-auto max-w-3xl rounded-[1.4rem] border border-[#EADBC8] bg-white p-6 shadow-sm">
          <h1 className="text-xl font-black text-[#5D4D46]">
            {detailError === "not_found"
              ? "このナレッジは見つかりませんでした。"
              : "ナレッジの詳細を読み込めませんでした。"}
          </h1>
          <p role="alert" className="mt-3 text-sm leading-6 text-[#6B5851]">
            {detailError === "not_found"
              ? "削除済みか、表示できないナレッジです。"
              : "時間をおいて、もう一度お試しください。"}
          </p>
          <Link
            to="/request/knowledge"
            className="mt-5 inline-flex min-h-[44px] items-center rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851]"
          >
            ナレッジライブラリへ戻る
          </Link>
        </section>
      </main>
    );
  }

  const displayName = getKnowledgeItemDisplayName(item);
  const groupTalkUsable = isKnowledgeItemGroupTalkUsable(item);
  const retryAiPreparation = canRetryKnowledgeItemAiPreparation(item);
  const canRetry = retryAiPreparation
    || (item.type === "url" && item.status === "failed" && item.can_retry)
    || (item.type === "pdf" && item.status === "failed" && item.can_retry)
    || (item.type === "executive_memo" && item.status === "failed" && Boolean(item.source_id));
  const showSummaryStatus = shouldShowKnowledgeSummaryStatus(item);
  const originalFilename = item.type === "pdf" ? item.original_filename?.trim() : null;

  return (
    <main className="min-h-screen min-w-0 overflow-x-hidden bg-[#FFF8F2] px-3 py-6 sm:px-6 sm:py-8">
      <div className="mx-auto grid w-full max-w-5xl min-w-0 gap-5">
        <Link
          to="/request/knowledge"
          className="inline-flex min-h-[44px] w-fit max-w-full items-center rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851]"
        >
          ナレッジライブラリへ戻る
        </Link>

        <header className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm sm:p-6">
          <div className="flex min-w-0 flex-wrap gap-2">
            <span className="rounded-full bg-[#FFF3E9] px-3 py-1 text-xs font-black text-[#A46D4C]">
              種別: {getKnowledgeTypeLabel(item.type)}
            </span>
            <span className="rounded-full bg-[#F5EEE7] px-3 py-1 text-xs font-black text-[#7C5A4E]">
              状態: {getKnowledgeLibraryStatusLabel(item)}
            </span>
            {showSummaryStatus ? (
              <span className="rounded-full bg-[#F7F5ED] px-3 py-1 text-xs font-black text-[#6F7462]">
                {getKnowledgeSummaryStatusLabel(item)}
              </span>
            ) : null}
          </div>
          <h1 className="mt-4 min-w-0 break-words text-2xl font-black text-[#5D4D46] [overflow-wrap:anywhere] sm:text-3xl">
            {displayName}
          </h1>
          {item.type === "url" && item.domain ? (
            <p className="mt-2 min-w-0 break-words text-sm font-bold text-[#8F7468] [overflow-wrap:anywhere]">
              {item.domain}
            </p>
          ) : null}
        </header>

        <section
          aria-labelledby="knowledge-basic-heading"
          className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm"
        >
          <h2 id="knowledge-basic-heading" className="text-lg font-black text-[#5D4D46]">基本情報</h2>
          <dl className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-black text-[#A67C52]">種別</dt>
              <dd className="mt-1 text-sm font-bold text-[#4A3E39]">{getKnowledgeTypeLabel(item.type)}</dd>
            </div>
            <div>
              <dt className="text-xs font-black text-[#A67C52]">ページ数</dt>
              <dd className="mt-1 text-sm font-bold text-[#4A3E39]">{item.page_count ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs font-black text-[#A67C52]">登録日時</dt>
              <dd className="mt-1 text-sm font-bold text-[#4A3E39]">{formatDateTime(item.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs font-black text-[#A67C52]">更新日時</dt>
              <dd className="mt-1 text-sm font-bold text-[#4A3E39]">{formatDateTime(item.updated_at)}</dd>
            </div>
            {originalFilename ? (
              <div className="min-w-0 sm:col-span-2">
                <dt className="text-xs font-black text-[#A67C52]">元ファイル名</dt>
                <dd className="mt-1 break-words text-sm font-bold text-[#4A3E39] [overflow-wrap:anywhere]">
                  {originalFilename}
                </dd>
              </div>
            ) : null}
            {item.type === "url" && item.domain ? (
              <div className="min-w-0 sm:col-span-2">
                <dt className="text-xs font-black text-[#A67C52]">ドメイン</dt>
                <dd className="mt-1 break-words text-sm font-bold text-[#4A3E39] [overflow-wrap:anywhere]">
                  {item.domain}
                </dd>
              </div>
            ) : null}
          </dl>
        </section>

        <section
          aria-labelledby="knowledge-ai-heading"
          className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm"
        >
          <h2 id="knowledge-ai-heading" className="text-lg font-black text-[#5D4D46]">AIでの利用状況</h2>
          <p className="mt-3 text-base font-black text-[#7C5A4E]">
            {getKnowledgeAiUsageStatusLabel(item)}
          </p>
          {item.status === "failed" ? (
            <p role="alert" className="mt-2 break-words text-sm leading-6 text-[#A45645] [overflow-wrap:anywhere]">
              {getKnowledgeFailureDisplayMessage(item)}
            </p>
          ) : isKnowledgeItemAiPreparationFailed(item) ? (
            <p role="alert" className="mt-2 break-words text-sm leading-6 text-[#A45645] [overflow-wrap:anywhere]">
              {getKnowledgeAiPreparationFailureMessage(item.ai_preparation_failure_code)}
            </p>
          ) : isKnowledgeItemPending(item) ? (
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">
              {item.status === "ready"
                ? "AIで利用するための準備をしています。"
                : "本文を読み取っています。"}
            </p>
          ) : null}
          {pollingTimedOut ? (
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">
              準備に時間がかかっています。しばらくしてから画面を再読み込みして状態を確認してください。
            </p>
          ) : null}
          {!groupTalkUsable && item.status === "ready" ? (
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">準備が完了するまでAI壁打ちでは利用できません。</p>
          ) : null}
        </section>

        <SummarySection item={item} />

        <section
          aria-labelledby="knowledge-preview-heading"
          className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm"
        >
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <h2 id="knowledge-preview-heading" className="text-lg font-black text-[#5D4D46]">抽出した本文</h2>
              <p className="mt-1 text-xs leading-5 text-[#8F7468]">
                文字数上限付きのプレビューです。全文の表示を保証するものではありません。
              </p>
            </div>
          </div>

          {!previewable ? (
            <p className="mt-4 text-sm leading-6 text-[#6B5851]">
              {item.status === "failed"
                ? "本文を表示できません。"
                : "本文プレビューはまだ表示できません。"}
            </p>
          ) : pagesLoading && pages.length === 0 ? (
            <p role="status" className="mt-4 text-sm font-bold text-[#6B5851]">本文プレビューを読み込み中です...</p>
          ) : pagesError && pages.length === 0 ? (
            <div className="mt-4">
              <p role="alert" className="text-sm leading-6 text-[#A45645]">
                {pageErrorMessage(pagesError, false)}
              </p>
              <button
                type="button"
                onClick={retryPages}
                className="mt-3 min-h-[44px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851]"
              >
                本文プレビューを再読み込み
              </button>
            </div>
          ) : pagesLoaded && pages.length === 0 ? (
            <p className="mt-4 text-sm leading-6 text-[#6B5851]">抽出本文がありません。</p>
          ) : (
            <div className="mt-4 grid min-w-0 gap-4">
              {pages.map((page, index) => (
                <PagePreviewCard
                  key={`${page.page_no ?? "none"}-${page.chunk_index}-${index}`}
                  itemType={item.type}
                  page={page}
                  index={index}
                />
              ))}
            </div>
          )}

          {pagesError && pages.length > 0 ? (
            <div className="mt-4 rounded-xl bg-[#FFF0EB] p-3">
              <p role="alert" className="text-sm font-bold text-[#A45645]">
                {pageErrorMessage(pagesError, true)}
              </p>
              {nextCursor ? (
                <button
                  type="button"
                  onClick={retryPages}
                  disabled={loadingMore}
                  className="mt-3 min-h-[44px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
                >
                  続きを再読み込み
                </button>
              ) : null}
            </div>
          ) : null}

          {nextCursor && !pagesError ? (
            <button
              type="button"
              onClick={() => void loadMorePages()}
              disabled={loadingMore}
              className="mt-5 min-h-[44px] w-full rounded-full border border-[#D9C5B9] bg-white px-5 py-2 text-sm font-black text-[#6B5851] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {loadingMore ? "読み込み中..." : "さらに本文を読み込む"}
            </button>
          ) : null}
        </section>

        <section
          aria-labelledby="knowledge-actions-heading"
          className="min-w-0 rounded-[1.4rem] border border-[#EADBC8] bg-white p-5 shadow-sm"
        >
          <h2 id="knowledge-actions-heading" className="text-lg font-black text-[#5D4D46]">操作</h2>
          {retryMessage ? <p role="status" className="mt-3 text-sm font-bold text-[#587653]">{retryMessage}</p> : null}
          {retryError ? <p role="alert" className="mt-3 text-sm font-bold text-[#A45645]">{retryError}</p> : null}
          <div className="mt-4 flex min-w-0 flex-col flex-wrap gap-3 sm:flex-row">
            <button
              type="button"
              onClick={startWallTalk}
              disabled={!groupTalkUsable || retrying || deleting}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full bg-[#5D4D46] px-5 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-[#B9AAA2] disabled:opacity-70"
            >
              このナレッジで壁打ちする
            </button>
            {canRetry ? (
              <button
                type="button"
                onClick={() => void handleRetry()}
                disabled={retrying || deleting}
                className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#C9B7A7] bg-white px-5 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                {retrying
                  ? "開始中..."
                  : retryAiPreparation
                    ? "要約を再試行"
                    : item.type === "pdf"
                      ? "PDFをもう一度処理"
                      : item.type === "executive_memo"
                        ? "社長メモから更新"
                        : "再試行"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={retrying || deleting}
              className="min-h-[44px] max-w-full whitespace-normal rounded-full border border-[#E2B9A9] bg-white px-5 py-2 text-sm font-black text-[#A45645] disabled:opacity-60"
            >
              ナレッジを削除
            </button>
          </div>
        </section>
      </div>

      {deleteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#2F2521]/50 px-3 py-5">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-detail-delete-heading"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[1.4rem] bg-white p-5 shadow-xl"
          >
            <h2 id="knowledge-detail-delete-heading" className="break-words text-lg font-black text-[#5D4D46]">
              このナレッジをライブラリから削除しますか？
            </h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              「{displayName}」を削除します。この操作は取り消せません。
            </p>
            {deleteError ? <p role="alert" className="mt-3 text-sm font-bold text-[#A45645]">{deleteError}</p> : null}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
                className="min-h-[44px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleting}
                className="min-h-[44px] rounded-full bg-[#A45645] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
              >
                {deleting ? "削除中..." : "ライブラリから削除"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
